export const VOICE_TRIM_WINDOW_MS = 10;
export const VOICE_TRIM_LEADING_PAD_MS = 80;
export const VOICE_TRIM_TRAILING_PAD_MS = 180;

const MIN_SPEECH_PEAK_RMS = 0.012;
const MIN_ACTIVE_RMS = 0.003;
const NOISE_MULTIPLIER = 2.5;
const PEAK_THRESHOLD_RATIO = 0.05;
const CLUSTER_WINDOW_COUNT = 3;
const MIN_ACTIVE_WINDOWS_PER_CLUSTER = 2;

export interface TrimmedPcm {
  samples: Float32Array;
  sampleRate: number;
  detectedSpeech: boolean;
  sourceDurationMs: number;
  durationMs: number;
  leadingTrimMs: number;
  trailingTrimMs: number;
  analysisThreshold: number;
}

export interface CleanedVoiceRecording {
  blob: Blob;
  cleanupMode: "trimmed" | "raw-fallback";
  cleanupDiagnostic?: string;
  detectedSpeech: true | null;
  sourceDurationMs: number;
  durationMs: number;
  leadingTrimMs: number;
  trailingTrimMs: number;
  analysisThreshold?: number;
}

const MIN_RAW_FALLBACK_BYTES = 256;
const MIN_RAW_FALLBACK_DURATION_MS = 250;

function percentile(values: number[], ratio: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) * ratio)];
}

function hasActiveCluster(active: boolean[], start: number, direction: 1 | -1): number {
  for (
    let index = start;
    direction === 1 ? index < active.length : index >= 0;
    index += direction
  ) {
    let activeCount = 0;
    for (let offset = 0; offset < CLUSTER_WINDOW_COUNT; offset += 1) {
      const candidate = index + offset * direction;
      if (candidate >= 0 && candidate < active.length && active[candidate]) activeCount += 1;
    }
    if (activeCount >= MIN_ACTIVE_WINDOWS_PER_CLUSTER) return index;
  }
  return -1;
}

function mixToMono(channels: Float32Array[]): Float32Array {
  const length = Math.min(...channels.map((channel) => channel.length));
  const mono = new Float32Array(length);
  for (let sampleIndex = 0; sampleIndex < length; sampleIndex += 1) {
    let sum = 0;
    for (const channel of channels) sum += channel[sampleIndex];
    mono[sampleIndex] = sum / channels.length;
  }
  return mono;
}

export function trimPcmChannels(channels: Float32Array[], sampleRate: number): TrimmedPcm {
  if (channels.length === 0 || sampleRate <= 0 || channels.some((channel) => channel.length === 0)) {
    throw new Error("A non-empty PCM recording is required.");
  }

  const mono = mixToMono(channels);
  const sourceDurationMs = (mono.length / sampleRate) * 1_000;
  const windowSize = Math.max(1, Math.round((sampleRate * VOICE_TRIM_WINDOW_MS) / 1_000));
  const windowRms: number[] = [];

  for (let start = 0; start < mono.length; start += windowSize) {
    const end = Math.min(start + windowSize, mono.length);
    let squareSum = 0;
    for (let index = start; index < end; index += 1) {
      squareSum += mono[index] * mono[index];
    }
    windowRms.push(Math.sqrt(squareSum / Math.max(1, end - start)));
  }

  const peakRms = Math.max(...windowRms);
  const noiseFloor = percentile(windowRms, 0.2);
  const noiseThreshold = Math.min(noiseFloor * NOISE_MULTIPLIER, peakRms * 0.55);
  const threshold = Math.max(MIN_ACTIVE_RMS, noiseThreshold, peakRms * PEAK_THRESHOLD_RATIO);
  const noSpeechResult: TrimmedPcm = {
    samples: mono,
    sampleRate,
    detectedSpeech: false,
    sourceDurationMs,
    durationMs: sourceDurationMs,
    leadingTrimMs: 0,
    trailingTrimMs: 0,
    analysisThreshold: threshold
  };

  if (!Number.isFinite(peakRms) || peakRms < MIN_SPEECH_PEAK_RMS) return noSpeechResult;

  const active = windowRms.map((rms) => rms >= threshold);
  const firstActiveWindow = hasActiveCluster(active, 0, 1);
  const lastActiveWindow = hasActiveCluster(active, active.length - 1, -1);
  if (firstActiveWindow < 0 || lastActiveWindow < 0) return noSpeechResult;

  const leadingPadSamples = Math.round(
    (sampleRate * VOICE_TRIM_LEADING_PAD_MS) / 1_000
  );
  const trailingPadSamples = Math.round(
    (sampleRate * VOICE_TRIM_TRAILING_PAD_MS) / 1_000
  );
  const startSample = Math.max(
    0,
    firstActiveWindow * windowSize - leadingPadSamples
  );
  const endSample = Math.min(
    mono.length,
    (lastActiveWindow + 1) * windowSize + trailingPadSamples
  );
  const samples = mono.slice(startSample, Math.max(startSample + 1, endSample));
  const leadingTrimMs = (startSample / sampleRate) * 1_000;
  const trailingTrimMs = ((mono.length - endSample) / sampleRate) * 1_000;

  return {
    samples,
    sampleRate,
    detectedSpeech: true,
    sourceDurationMs,
    durationMs: (samples.length / sampleRate) * 1_000,
    leadingTrimMs,
    trailingTrimMs,
    analysisThreshold: threshold
  };
}

export function encodeMonoWav(samples: Float32Array, sampleRate: number): Blob {
  const bytesPerSample = 2;
  const headerBytes = 44;
  const dataBytes = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(headerBytes + dataBytes);
  const view = new DataView(buffer);

  const writeAscii = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };

  writeAscii(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(8, "WAVE");
  writeAscii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeAscii(36, "data");
  view.setUint32(40, dataBytes, true);

  for (let index = 0; index < samples.length; index += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[index]));
    const pcm = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    view.setInt16(headerBytes + index * bytesPerSample, Math.round(pcm), true);
  }

  return new Blob([buffer], { type: "audio/wav" });
}

function cleanupDiagnostic(reason: unknown): string {
  if (reason instanceof DOMException) return reason.name || "DOMException";
  if (reason instanceof Error) return reason.name || "Error";
  return "UnknownCleanupError";
}

export function preserveRawVoiceRecording(
  blob: Blob,
  sourceDurationMs: number,
  diagnostic: string
): CleanedVoiceRecording {
  if (
    blob.size < MIN_RAW_FALLBACK_BYTES ||
    !Number.isFinite(sourceDurationMs) ||
    sourceDurationMs < MIN_RAW_FALLBACK_DURATION_MS
  ) {
    throw new Error("INVALID_RECORDING");
  }

  return {
    blob,
    cleanupMode: "raw-fallback",
    cleanupDiagnostic: diagnostic,
    detectedSpeech: null,
    sourceDurationMs,
    durationMs: sourceDurationMs,
    leadingTrimMs: 0,
    trailingTrimMs: 0,
    analysisThreshold: 0
  };
}

/**
 * Trims PCM captured live from the microphone, with no decode step at all.
 *
 * This is the primary path. Compressing to MP4/WebM and decoding back just to
 * measure loudness was a round trip we never needed — the output is a WAV
 * either way — and `decodeAudioData` refusing a browser's OWN MediaRecorder
 * output is exactly what silently disabled trimming on iOS: every take fell
 * through to the untrimmed fallback, no matter how much silence it carried.
 */
export function cleanPcmRecording(
  channels: Float32Array[],
  sampleRate: number
): CleanedVoiceRecording {
  const trimmed = trimPcmChannels(channels, sampleRate);
  if (!trimmed.detectedSpeech) throw new Error("NO_SPEECH_DETECTED");

  return {
    blob: encodeMonoWav(trimmed.samples, trimmed.sampleRate),
    cleanupMode: "trimmed",
    detectedSpeech: true,
    sourceDurationMs: trimmed.sourceDurationMs,
    durationMs: trimmed.durationMs,
    leadingTrimMs: trimmed.leadingTrimMs,
    trailingTrimMs: trimmed.trailingTrimMs,
    analysisThreshold: trimmed.analysisThreshold
  };
}

export async function cleanVoiceRecording(
  blob: Blob,
  sourceDurationMs: number
): Promise<CleanedVoiceRecording> {
  if (
    blob.size < MIN_RAW_FALLBACK_BYTES ||
    !Number.isFinite(sourceDurationMs) ||
    sourceDurationMs < MIN_RAW_FALLBACK_DURATION_MS
  ) {
    throw new Error("INVALID_RECORDING");
  }

  const AudioContextConstructor =
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) {
    return preserveRawVoiceRecording(blob, sourceDurationMs, "AudioContextUnavailable");
  }

  let context: AudioContext | null = null;
  try {
    context = new AudioContextConstructor();
    const encodedAudio = await blob.arrayBuffer();
    const decoded = await context.decodeAudioData(encodedAudio.slice(0));
    const channels = Array.from(
      { length: decoded.numberOfChannels },
      (_, channelIndex) => decoded.getChannelData(channelIndex)
    );
    const trimmed = trimPcmChannels(channels, decoded.sampleRate);
    if (!trimmed.detectedSpeech) throw new Error("NO_SPEECH_DETECTED");

    return {
      blob: encodeMonoWav(trimmed.samples, trimmed.sampleRate),
      cleanupMode: "trimmed",
      detectedSpeech: true,
      sourceDurationMs: trimmed.sourceDurationMs,
      durationMs: trimmed.durationMs,
      leadingTrimMs: trimmed.leadingTrimMs,
      trailingTrimMs: trimmed.trailingTrimMs,
      analysisThreshold: trimmed.analysisThreshold
    };
  } catch (reason) {
    if (reason instanceof Error && reason.message === "NO_SPEECH_DETECTED") throw reason;
    return preserveRawVoiceRecording(blob, sourceDurationMs, cleanupDiagnostic(reason));
  } finally {
    if (context) void context.close();
  }
}
